import { CALC_SOURCES, COLUMN_CATALOG_MAP, ORION_ENTITY_GROUPINGS } from './constants'
import { DERIVE_REGISTRY, getMergedDependencyOpts } from './derivedColumns'
import { resolveColumnOptions } from './calcResolve'

export { resolveColumnOptions } from './calcResolve'

function isDerivedColumn (columnType) {
  return COLUMN_CATALOG_MAP[columnType]?.kind === 'derived'
}

function isWindowColumn (columnType) {
  return COLUMN_CATALOG_MAP[columnType]?.kind === 'window'
}

function isComparisonColumn (columnType) {
  return COLUMN_CATALOG_MAP[columnType]?.kind === 'comparison'
}

function isClientOnlyColumn (columnType) {
  return isDerivedColumn(columnType) || isWindowColumn(columnType) || isComparisonColumn(columnType)
}

function syntheticIdForCalcKey (calcKey) {
  return `__req_${calcKey.replace(/[^a-zA-Z0-9]+/g, '_')}`
}

/**
 * Appends synthetic material column instances so every distinct calcKey required by
 * derived formulas is present in resolveCalcAssignments (Orion dedupes by key).
 */
export function expandColumnsWithDerivedDeps (columns, globalDateConfig) {
  const material = columns.filter(c => !isClientOnlyColumn(c.columnType))
  const materialAssignments = resolveCalcAssignments(material, globalDateConfig)
  const coveredKeys = new Set(materialAssignments.keys())
  const synthetics = []

  for (const col of columns) {
    const catalog = COLUMN_CATALOG_MAP[col.columnType]
    if (!catalog || catalog.kind !== 'derived') continue
    const def = DERIVE_REGISTRY[catalog.deriveId]
    if (!def?.calcInputs?.length) continue

    for (const input of def.calcInputs) {
      const source = CALC_SOURCES[input.source]
      if (!source) continue
      const mergedOpts = getMergedDependencyOpts(input, col.options)
      if (!mergedOpts) continue
      const resolved = resolveColumnOptions(mergedOpts, globalDateConfig)
      const key = source.calcKey(resolved)
      if (coveredKeys.has(key)) continue
      coveredKeys.add(key)
      synthetics.push({
        columnType: input.representativeColumnType,
        id: syntheticIdForCalcKey(key),
        options: mergedOpts
      })
    }
  }

  return synthetics.length > 0 ? [...columns, ...synthetics] : columns
}

/**
 * Groups columns by calcKey, deduplicating so that columns sharing the same
 * calc source + resolved options produce a single Orion calculation.
 * Returns { calcs: OrionCalcObject[], calcAssignments: Map<calcKey, { calcId, source, resolvedOptions, columnIds[] }> }
 */
export function resolveCalcAssignments (columns, globalDateConfig) {
  const groups = new Map()
  let calcIdCounter = 0

  for (const col of columns) {
    const catalog = COLUMN_CATALOG_MAP[col.columnType]
    if (!catalog) continue
    if (isClientOnlyColumn(col.columnType)) continue
    const source = CALC_SOURCES[catalog.calcSource]
    if (!source) continue

    const resolved = resolveColumnOptions(col.options, globalDateConfig)
    const key = source.calcKey(resolved)

    if (!groups.has(key)) {
      const calcId = `calc_${calcIdCounter++}`
      groups.set(key, {
        calcId,
        sourceName: catalog.calcSource,
        source,
        resolvedOptions: resolved,
        columnIds: []
      })
    }
    groups.get(key).columnIds.push(col.id)
  }

  return groups
}

function buildCalcs (columns, globalDateConfig) {
  const expanded = expandColumnsWithDerivedDeps(columns, globalDateConfig)
  const groups = resolveCalcAssignments(expanded, globalDateConfig)
  const calcs = []

  for (const group of groups.values()) {
    calcs.push(group.source.buildCalculation(group.calcId, group.resolvedOptions))
  }

  return calcs
}

function nestGroupings (groupings, calcs) {
  return [
    groupings.reduceRight((innerCalcs, grouping, index) => [{
      id: `grouping_${index}_${grouping}`,
      $type: 'grouping',
      grouping,
      calculations: [...calcs, ...innerCalcs]
    }], [])[0]
  ]
}

/**
 * Builds the Orion Reporting/Scope request body from user config.
 * Includes entity/entityIds for Orion scoping. The frontend owns entity resolution.
 */
export function buildOrionRequest ({ groupings, columns, dateMode, quickDate, startDate, endDate }, { asOfDate, maxGroupingDepth, entity, entityIds }) {
  const globalDateConfig = { dateMode, quickDate, startDate, endDate }
  const calcs = buildCalcs(columns, globalDateConfig)
  const effectiveGroupings = maxGroupingDepth != null
    ? groupings.slice(0, maxGroupingDepth)
    : groupings

  const request = { entity, entityIds, asOfDate }

  if (effectiveGroupings.length === 0) {
    request.calculations = calcs
  } else {
    request.calculations = [...calcs, ...nestGroupings(effectiveGroupings, calcs)]
  }

  return request
}

/**
 * Builds a child request for lazy expand — uses remaining groupings
 * starting from the given depth index, scoped to a specific group entity.
 * Pass maxGroupingDepth to limit how many grouping levels are included.
 */
export function buildChildRequest ({ groupings, columns, dateMode, quickDate, startDate, endDate }, { asOfDate, depthIndex, maxGroupingDepth, entity, entityIds }) {
  const globalDateConfig = { dateMode, quickDate, startDate, endDate }
  const calcs = buildCalcs(columns, globalDateConfig)
  const remainingGroupings = groupings.slice(depthIndex)
  const effectiveGroupings = maxGroupingDepth != null
    ? remainingGroupings.slice(0, maxGroupingDepth)
    : remainingGroupings

  const request = { entity, entityIds, asOfDate }

  if (effectiveGroupings.length === 0) {
    request.calculations = calcs
  } else {
    request.calculations = nestGroupings(effectiveGroupings, calcs)
  }

  return request
}

/**
 * Builds a child request for virtual (non-entity-scopable) groupings.
 * Scopes to the nearest entity-scopable ancestor and adds a string-equal
 * filter on the virtual grouping name to narrow results server-side.
 * Pass maxGroupingDepth to limit how many inner grouping levels are included.
 */
export function buildFilteredChildRequest ({ groupings, columns, dateMode, quickDate, startDate, endDate }, {
  asOfDate,
  depthIndex,
  maxGroupingDepth,
  entity,
  entityIds,
  filterGrouping,
  filterName
}) {
  const globalDateConfig = { dateMode, quickDate, startDate, endDate }
  const calcs = buildCalcs(columns, globalDateConfig)
  const remainingGroupings = groupings.slice(depthIndex)
  const effectiveGroupings = maxGroupingDepth != null
    ? remainingGroupings.slice(0, maxGroupingDepth)
    : remainingGroupings

  if (effectiveGroupings.length === 0) {
    return { entity, entityIds, asOfDate, calculations: calcs }
  }

  const innerGrouping = nestGroupings(effectiveGroupings, calcs)[0]

  const wrappedCalc = {
    id: `filter_${filterGrouping}`,
    $type: 'grouping',
    grouping: filterGrouping,
    filters: [{
      $type: 'string-equal',
      path: ORION_ENTITY_GROUPINGS.has(filterGrouping)
        ? `group-info:${filterGrouping}:Name`
        : 'group-info',
      haystack: [filterName]
    }],
    calculations: [innerGrouping]
  }

  return { entity, entityIds, asOfDate, calculations: [wrappedCalc] }
}
